import { getDefaultState } from './defaultState'
import { getters } from './getters'

function calculateSettableValue (state, fieldName, value) {
  /**
   * If the given value is out of bounds regarding min and max values for this field, set the default value instead
   */
  const maxValue = getters().calculatorMax(state)[fieldName]() || Infinity
  const minValue = getters().calculatorMin(state)[fieldName]()
  const defaultValue = getters().calculatorDefault(state)[fieldName]()
  if (typeof (value) === 'object') {
    value = value.value
  }
  if (value < minValue || value > maxValue) {
    return defaultValue
  } else {
    return value
  }
}

export const mutations = () => {
  return {
    /*
    * local Storage Functions
    */
    setLocalstorageAndUrl (state, payload) {
      // Places Store into local Storage an append base64 to URL
      // eslint-disable-next-line
      const { key, value } = payload
      const localStorageB64 = btoa(localStorage['sto-jalousie-konfigurator'])
      state.calculatorData.base64Link = localStorageB64
      const params = new URLSearchParams(location.search)
      params.set('state', localStorageB64)
      window.history.replaceState({}, '', `${location.pathname}?${params}`)
    },

    /*
    * Tab / Page / Step Navigation
    */
    setCurrentPage (state, payload) {
      if (!isNaN(parseInt(payload.value))) {
        state.calculatorData.currentPageNumber = parseInt(payload.value)
      }
    },
    incrementPage (state) {
      state.calculatorData.currentPageNumber++
    },
    decrementPage (state) {
      state.calculatorData.currentPageNumber--
    },

    /*
    * Validation:
    * Check fields
    * Disable tabs if there is no input done
    * Set errors
    */
    updateTabsSettings (state) {
      // Goal: update all Tab Settings
      // check if all Fields in this Tab are manipulated

      let errorOccurs = false
      for (const [pageIndex, page] of state.calculatorData.calculatorPages.entries()) {
        const inputFieldsInputStates = page.inputFields.map((field, fieldIndex) => field.didInput)
        page.allFieldsUpdated = inputFieldsInputStates.every(x => x === true)

        if ((page.errors) && (pageIndex <= state.calculatorData.allowStepUpTo)) {
          errorOccurs = true
        }

        if (page.allFieldsUpdated) {
          state.calculatorData.allowStepUpTo = pageIndex + 1
          if (state.calculatorData.calculatorPages[pageIndex + 1]) {
            state.calculatorData.calculatorPages[pageIndex + 1].disabled = false
          }
        } else {
          break
        }
      }
      if (errorOccurs) {
        if (state.calculatorData.allowStepUpTo !== 9) {
          state.calculatorData.calculatorPages[state.calculatorData.allowStepUpTo].disabled = true
        }
      }
    },
    setErrors (state) {
      if (state.calculatorData.currentOrder.elementForm !== '') {
        const elementFormData = require('@/configfiles/elementforms/' + state.calculatorData.currentOrder.elementForm + '.js')
        let errors = elementFormData.getErrors(state.calculatorData.currentOrder)
        state.calculatorData.errors = errors

        // set errors ralated to elementLengths
        state.calculatorData.calculatorPages[2].errors = !!(errors.a || errors.a1 || errors.a2)
        state.calculatorData.calculatorPages[3].errors = !!(errors.c || errors.d1 || errors.d2)
        state.calculatorData.calculatorPages[4].errors = !!(errors.e || errors.b || errors.w || errors.h)
        state.calculatorData.calculatorPages[6].errors = !!(errors.g || errors.f)
      }
    },
    setWarnings (state) {
      if (state.calculatorData.currentOrder.elementForm !== '') {
        const elementFormData = require('@/configfiles/elementforms/' + state.calculatorData.currentOrder.elementForm + '.js')
        state.calculatorData.warnings = elementFormData.getWarnings(state.calculatorData.currentOrder)
      }
    },
    resetState (state, payload = null) {
      const initialState = getDefaultState()
      Object.keys(initialState).forEach(key => {
        state[key] = initialState[key]
      })
      /**
       * When a payload is given, this means that we want to keep the information from the order element before. If it is not, the state has been resetted with the reset button
       */
      if (payload) {
        state.orderBackup = {}
        Object.keys(payload).forEach(key => {
          state.orderBackup[key] = payload[key]
        })
      } else {
        state.recentlyResetted = true
      }
    },
    resetCalculatorPages (state) {
      const initialCalculatorPages = Object.assign({}, getDefaultState()['calculatorData']['calculatorPages'])
      Object.keys(initialCalculatorPages).forEach(key => {
        state.calculatorData.calculatorPages[key] = initialCalculatorPages[key]
      })
      state.calculatorData.currentOrder.elementForm = ''
    },
    setFieldDefaults (state, payload) {
      const { pageIndex, fieldIndex, key: fieldName } = payload
      payload.fieldData.forEach((value, key) => {
        if (value) {
          // If a default field is overwritten, the current order is also set to this default
          if (key === 'default') {
            state.calculatorData.currentOrder[fieldName] = value
          }
          state.calculatorData.calculatorPages[pageIndex].inputFields[fieldIndex][key] = value
        }
      })
      state.calculatorData.calculatorPages[pageIndex].inputFields[fieldIndex].didInput = false
    },
    markFieldAsInputted (state, payload) {
      const { pageIndex, fieldIndex } = payload
      state.calculatorData.calculatorPages[pageIndex].inputFields[fieldIndex].didInput = true
    },
    markFieldAsNotInputted (state, payload) {
      // TODO refactor this together with markFieldAsInputted so that the value is set in the payload
      const { pageIndex, fieldIndex } = payload
      state.calculatorData.calculatorPages[pageIndex].inputFields[fieldIndex].didInput = false
    },
    updateCurrentOrder (state, payload) {
      const { key, value } = payload
      // TODO these values are the ones that can be set by the user and are also stored when a reset is done by resetting the elementForms element (see `currentOrderBackup` in `didInputFkt`). Put this into a configuration or inside the store default values to avoid duplicate code
      if (['a', 'b', 'c', 'd1', 'e'].includes(key)) {
        state.calculatorData.currentOrder[key] = calculateSettableValue(state, key, value)
      } else {
        state.calculatorData.currentOrder[key] = value
      }
    },
    updateResetStatus (state, payload) {
      state.recentlyResetted = payload.status
    },
    updatePageStatus (state, payload) {
      const { pageIndex, key, value } = payload
      state.calculatorData.calculatorPages[pageIndex][key] = value
    },
    deleteOrderBackup (state) {
      state.orderBackup = Object.assign({})
    },
    updateTinyLink (state, payload) {
      state.calculatorData.tinyLink = payload
    },
    setCurrentPageNumber (state, payload) {
      state.calculatorData.currentPageNumber = payload
    },
    merchandiserViewStateIncreasement (state) {
      state.calculatorData.merchandiserViewToggleState++
    }
  }
}
